import { Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, Inject, Injector, PLATFORM_ID, CUSTOM_ELEMENTS_SCHEMA, ViewEncapsulation } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { ApproachStepItem, ClientLogoItem, HeroSlideItem, PortfolioCaseItem, PresentationSlideItem, StatItem } from './home.models';
import { ApproachSectionComponent } from './sections/approach-section/approach-section.component';
import { FinalCtaSectionComponent } from './sections/final-cta-section/final-cta-section.component';
import { MetricsSectionComponent } from './sections/metrics-section/metrics-section.component';
import { PortfolioSectionComponent } from './sections/portfolio-section/portfolio-section.component';
import { PresentationSectionComponent } from './sections/presentation-section/presentation-section.component';
import { ServicesShowcaseSectionComponent } from './sections/services-showcase-section/services-showcase-section.component';
import { TestimonialSectionComponent } from './sections/testimonial-section/testimonial-section.component';
import { TrustCarouselSectionComponent } from './sections/trust-carousel-section/trust-carousel-section.component';
import { runAfterHydration } from '../shared/run-after-hydration';
import { initPageScrollReveal, refreshGlobalScrollTriggers } from '../shared/scroll-reveal.util';

/** Chart.js: clip plot left→right during grid phase; skip datasets until reveal completes */
let dingoChartRevealPluginRegistered = false;

function registerDingoChartRevealPlugin(Chart: any): void {
  if (dingoChartRevealPluginRegistered) return;
  dingoChartRevealPluginRegistered = true;

  const drawMovingLinePoint = (chart: any, progress: number): void => {
    const st = chart.$dingoReveal as { movingPointEnabled?: boolean; movingPointDatasetIndex?: number } | undefined;
    if (!st?.movingPointEnabled) return;

    const datasetIndex = st.movingPointDatasetIndex ?? 0;
    const meta = chart.getDatasetMeta?.(datasetIndex);
    const points = meta?.data ?? [];
    if (points.length < 2) return;

    const segmentProgress = Math.min(points.length - 1, Math.max(0, progress * (points.length - 1)));
    const startIndex = Math.min(points.length - 2, Math.floor(segmentProgress));
    const endIndex = startIndex + 1;
    const localProgress = segmentProgress - startIndex;
    const start = points[startIndex];
    const end = points[endIndex];
    if (!start || !end) return;

    const x = start.x + (end.x - start.x) * localProgress;
    const y = start.y + (end.y - start.y) * localProgress;
    const ctx = chart.ctx;

    ctx.save();
    ctx.beginPath();
    ctx.arc(x, y, 9, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(142, 255, 47, 0.18)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, 5.4, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(142, 255, 47, 0.96)';
    ctx.shadowColor = 'rgba(116, 252, 3, 0.74)';
    ctx.shadowBlur = 14;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(x, y, 2.15, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.shadowBlur = 0;
    ctx.fill();
    ctx.restore();
  };

  Chart.register({
    id: 'dingoChartReveal',
    beforeDraw(chart: any) {
      const st = chart.$dingoReveal as { gridProgress: number; datasetsEnabled: boolean } | undefined;
      if (!st || st.gridProgress >= 1) return;
      const { ctx, chartArea } = chart;
      if (!chartArea) return;
      ctx.save();
      ctx.beginPath();
      const w = Math.max(0, chartArea.width * st.gridProgress);
      ctx.rect(chartArea.left, chartArea.top, w, chartArea.height);
      ctx.clip();
    },
    beforeDatasetsDraw(chart: any): boolean | undefined {
      const st = chart.$dingoReveal as {
        datasetsEnabled: boolean;
        dataProgress?: number;
        clipDatasets?: boolean;
        barFillEnabled?: boolean;
        barFillProgress?: number;
      } | undefined;
      if (st && !st.datasetsEnabled) {
        return false;
      }
      if (st && st.barFillEnabled && st.datasetsEnabled && typeof st.barFillProgress === 'number' && st.barFillProgress < 1) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return undefined;
        const h = Math.max(0, chartArea.height * st.barFillProgress);
        ctx.save();
        ctx.beginPath();
        ctx.rect(chartArea.left, chartArea.bottom - h, chartArea.width, h);
        ctx.clip();
      }
      if (st && st.clipDatasets && st.datasetsEnabled && typeof st.dataProgress === 'number' && st.dataProgress < 1) {
        const { ctx, chartArea } = chart;
        if (!chartArea) return undefined;
        ctx.save();
        ctx.beginPath();
        const w = Math.max(0, chartArea.width * st.dataProgress);
        ctx.rect(chartArea.left, chartArea.top, w, chartArea.height);
        ctx.clip();
      }
      return undefined;
    },
    afterDatasetsDraw(chart: any) {
      const st = chart.$dingoReveal as {
        datasetsEnabled: boolean;
        dataProgress?: number;
        clipDatasets?: boolean;
        barFillEnabled?: boolean;
        barFillProgress?: number;
      } | undefined;
      if (st && st.barFillEnabled && st.datasetsEnabled && typeof st.barFillProgress === 'number' && st.barFillProgress < 1) {
        chart.ctx.restore();
      }
      if (st && st.clipDatasets && st.datasetsEnabled && typeof st.dataProgress === 'number' && st.dataProgress < 1) {
        chart.ctx.restore();
      }
      if (st && st.datasetsEnabled && typeof st.dataProgress === 'number') {
        drawMovingLinePoint(chart, st.dataProgress);
      }
    },
    afterDraw(chart: any) {
      const st = chart.$dingoReveal as { gridProgress: number } | undefined;
      if (!st || st.gridProgress >= 1) return;
      chart.ctx.restore();
    }
  });
}

@Component({
  selector: 'app-home',
  standalone: true,
  templateUrl: './home.component.html',
  styleUrl: './home.component.scss',
  imports: [
    CommonModule,
    ApproachSectionComponent,
    FinalCtaSectionComponent,
    MetricsSectionComponent,
    PortfolioSectionComponent,
    PresentationSectionComponent,
    ServicesShowcaseSectionComponent,
    TestimonialSectionComponent,
    TrustCarouselSectionComponent
  ],
  encapsulation: ViewEncapsulation.None,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HomeComponent implements AfterViewInit, OnDestroy {
  @ViewChild('homeRoot') homeRoot!: ElementRef<HTMLElement>;
  @ViewChild(MetricsSectionComponent) metricsSection?: MetricsSectionComponent;
  @ViewChild(ApproachSectionComponent) approachSection?: ApproachSectionComponent;
  @ViewChild(PresentationSectionComponent) presentationSection?: PresentationSectionComponent;
  @ViewChild(TrustCarouselSectionComponent) trustCarouselSection?: TrustCarouselSectionComponent;

  private get statsBlock(): ElementRef<HTMLElement> | undefined {
    return this.metricsSection?.statsBlock;
  }

  private get approachTimeline(): ElementRef<HTMLElement> | undefined {
    return this.approachSection?.approachTimeline;
  }

  private get chartsPresentation(): ElementRef<HTMLElement> | undefined {
    return this.presentationSection?.chartsPresentation;
  }

  private get logoSwiper(): ElementRef<HTMLElement & { initialize: () => void }> | undefined {
    return this.trustCarouselSection?.logoSwiper;
  }

  private get prevBtn(): ElementRef<HTMLButtonElement> | undefined {
    return this.trustCarouselSection?.prevBtn;
  }

  private get nextBtn(): ElementRef<HTMLButtonElement> | undefined {
    return this.trustCarouselSection?.nextBtn;
  }

  stats: StatItem[] = [
    { target: 10, suffix: '+', label: 'años de experiencia' },
    { target: 50, suffix: '+', label: 'Clientes' },
    { target: 100, suffix: '+', label: 'proyectos ejecutados' }
  ];

  currentValues: number[] = [0, 0, 0];
  hasAnimated = false;
  activePresentationIndex = 0;
  heroTrackerRotateX = '0deg';
  heroTrackerRotateY = '0deg';
  heroTrackerOffsetX = '0px';
  heroTrackerOffsetY = '0px';
  heroTrackerGlowX = '50%';
  heroTrackerGlowY = '50%';
  private gsapLib: any = null;
  private counterAnimationFrame: number | null = null;
  private presentationSlideEls: HTMLElement[] = [];
  private presentationScrollTrigger: any = null;
  private presentationTransitionTl: any = null;
  private presentationChromeTl: any = null;
  private hasAnimatedPresentationBullets = false;
  private hasAnimatedPresentationSideUi = false;
  private hasDispatchedHeroIntroReady = false;
  private heroIntroReadyTimer: ReturnType<typeof setTimeout> | null = null;
  private deferredChartsTimer: ReturnType<typeof setTimeout> | null = null;
  private deferredBelowFoldTimer: ReturnType<typeof setTimeout> | null = null;
  private deferredChartHeroReadyListener: EventListener | null = null;
  private marketValueChart: any = null;
  private audienceBarCharts: { age: any; gender: any } | null = null;
  private impactBarChart: any = null;
  private statsObserver: IntersectionObserver | null = null;
  private revealObserver: IntersectionObserver | null = null;
  private gsapCleanups: Array<() => void> = [];
  private chartResizeObserver: ResizeObserver | null = null;
  private presentationProgrammaticTargetIndex: number | null = null;
  private presentationProgrammaticTargetTimer: number | null = null;
  private readonly chartGridRevealMs = 680;
  private readonly chartDataAnimMs = 1100;

  readonly clientLogos: ClientLogoItem[] = [
    { name: 'BBVA', file: 'bbva.png' },
    { name: 'Carajillo', file: 'carajillo.png' },
    { name: 'Delantal', file: 'delantal.png' },
    { name: 'ExxonMobile', file: 'exxonmobile.png' },
    { name: 'IMSS', file: 'imms.png' },
    { name: 'IOS Offices', file: 'ios_offices.png' },
    { name: 'Loreal', file: 'loreal.png' },
    { name: 'Moderna', file: 'moderna.png' },
    { name: 'Panamericana', file: 'panamericana.png' },
    { name: 'Pernod Ricard', file: 'pernod_ricard.png' },
    { name: 'Quaker', file: 'quaker.png' },
    { name: 'Qualcomm', file: 'qualcomm.png' },
    { name: 'Savinon', file: 'savinon.png' },
    { name: 'Sophia', file: 'sophia.png' },
    { name: 'TEDx', file: 'tedx.png' },
    { name: 'Tuborg', file: 'tuborg.png' },
    { name: 'Universal', file: 'universal.png' },
  ];

  readonly heroSlides: HeroSlideItem[] = [
    {
      id: 'growth-performance',
      caption: 'Marketing y tecnología para crecimiento real',
      title: 'Creamos sistemas que generan',
      highlight: 'clientes para tu negocio',
      subtitle:
        'Pauta, contenido, automatización, data y tecnología trabajando bajo un mismo objetivo: generar ventas y escalar resultados. Contratas crecimiento, no solo entregables.',
      image: 'assets/images/home/hero.png',
      imageAlt: 'Panel de rendimiento y campañas DINGO',
      primaryCtaText: 'Agenda tu diagnóstico estratégico',
      primaryCtaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20quiero%20agendar%20mi%20diagn%C3%B3stico%20gratuito%20con%20DINGO',
      secondaryCtaText: 'Ver servicios',
      secondaryCtaUrl: '#enfoque',
      cards: [
        { id: 'growth', label: 'Performance', value: 'ROAS', detail: 'Pauta optimizada a ventas', variant: 'growth', order: 4 },
        { id: 'strategy', label: 'Servicios', detail: 'Social · Search · CRM · Web', variant: 'strategy', order: 5 },
        { id: 'conversion', label: 'Embudos', value: 'CRO', detail: 'Más conversiones en tu sitio', variant: 'conversion', order: 6 },
        { id: 'revenue', label: 'Equipo DINGO', detail: 'Ejecución y reporting claro', variant: 'revenue', order: 7 },
      ]
    },
    {
      id: 'full-funnel',
      caption: 'Crecimiento con ROI',
      title: 'Cada peso debe',
      highlight: 'acercarte a resultados',
      subtitle:
        'Modelamos escenarios, optimizamos inversión y conectamos estrategia con ventas, leads y crecimiento real.',
      image: 'assets/images/illustrations/home/hero.png',
      imageAlt: 'Embudo de captación y resultados',
      primaryCtaText: 'Quiero mejorar mis resultados',
      primaryCtaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20quiero%20cotizar%20un%20proyecto%20de%20marketing%20con%20DINGO',
      secondaryCtaText: 'Ver casos de éxito',
      secondaryCtaUrl: '#casos-exito',
      cards: [
        { id: 'growth', label: 'Pauta', value: 'Meta · Google', detail: 'Inversión orientada a lead', variant: 'growth', order: 4 },
        { id: 'strategy', label: 'Automatización', detail: 'WhatsApp y email nurturing', variant: 'strategy', order: 5 },
        { id: 'conversion', label: 'Landing & forms', detail: 'Páginas que convierten', variant: 'conversion', order: 6 },
        { id: 'revenue', label: 'CRM', detail: 'Handoff limpio a ventas', variant: 'revenue', order: 7 },
      ]
    },
    {
      id: 'brand-growth',
      caption: 'Media y audiencias',
      title: 'Llega a quien',
      highlight: 'sí te compra',
      subtitle:
        'Segmentamos audiencias reales para que tu inversión llegue a clientes potenciales y no a tráfico sin intención.',
      image: 'assets/images/illustrations/home/hero.png',
      imageAlt: 'Identidad y creatividad orientadas a conversión',
      primaryCtaText: 'Necesito mejor segmentación',
      primaryCtaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20quiero%20informaci%C3%B3n%20sobre%20servicios%20de%20marca%20y%20creatividad%20con%20DINGO',
      secondaryCtaText: 'Ir a contacto',
      secondaryCtaUrl: '/contacto',
      cards: [
        { id: 'growth', label: 'Branding', detail: 'Posicionamiento y narrativa', variant: 'growth', order: 4 },
        { id: 'strategy', label: 'Creatividad', detail: 'Piezas para pauta y social', variant: 'strategy', order: 5 },
        { id: 'conversion', label: 'UX / Web', detail: 'Experiencias que convierten', variant: 'conversion', order: 6 },
        { id: 'revenue', label: 'Contenido', detail: 'Editorial y performance', variant: 'revenue', order: 7 },
      ]
    }
  ];

  readonly presentationHero: HeroSlideItem = this.heroSlides[0];

  readonly presentationSlides: PresentationSlideItem[] = [
    {
      id: 'hero-intro',
      eyebrow: 'Marketing y tecnología para crecimiento real',
      titleMain: 'Creamos sistemas que generan',
      titleOutline: 'clientes para tu negocio',
      description:
        'Pauta, contenido, automatización, data y tecnología trabajando bajo un mismo objetivo: generar ventas y escalar resultados. Contratas crecimiento, no solo entregables.',
      ctaText: 'Agenda tu diagnóstico estratégico',
      ctaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20quiero%20agendar%20mi%20diagn%C3%B3stico%20gratuito%20con%20DINGO',
      orbGradient: 'radial-gradient(circle at 34% 28%, rgba(168, 255, 92, 0.98) 0%, rgba(116, 252, 3, 0.9) 42%, rgba(79, 176, 1, 0.82) 72%, rgba(44, 98, 0, 0.35) 100%)',
      visual: 'assets/images/illustrations/services/hero.svg',
      visualAlt: 'Servicios de growth y performance DINGO'
    },
    {
      id: 'market-value',
      eyebrow: 'Crecimiento con ROI',
      titleMain: 'Cada peso debe',
      titleOutline: 'acercarte a resultados',
      description:
        'Modelamos escenarios, optimizamos inversión y conectamos estrategia con ventas, leads y crecimiento real.',
      ctaText: 'Quiero mejorar mis resultados',
      ctaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20quiero%20mejorar%20mis%20resultados%20de%20marketing%20con%20DINGO',
      orbGradient: 'radial-gradient(circle at 30% 25%, rgba(196, 255, 132, 0.95) 0%, rgba(135, 255, 47, 0.9) 40%, rgba(78, 182, 4, 0.8) 72%, rgba(34, 84, 0, 0.32) 100%)',
      visual: 'assets/images/illustrations/about/hero.svg',
      visualAlt: 'Visual de crecimiento y retorno de inversión'
    },
    {
      id: 'audience-behavior',
      eyebrow: 'Media y audiencias',
      titleMain: 'Llega a quien',
      titleOutline: 'sí te compra',
      description:
        'Segmentamos audiencias reales para que tu inversión llegue a clientes potenciales y no a tráfico sin intención.',
      ctaText: 'Necesito mejor segmentación',
      ctaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20necesito%20ayuda%20con%20audiencias%20y%20pauta%20con%20DINGO',
      orbGradient: 'radial-gradient(circle at 35% 28%, rgba(164, 247, 112, 0.95) 0%, rgba(116, 252, 3, 0.88) 46%, rgba(63, 146, 0, 0.8) 76%, rgba(23, 61, 0, 0.3) 100%)',
      visual: 'assets/images/illustrations/about/unique.svg',
      visualAlt: 'Análisis de audiencia para campañas'
    },
    {
      id: 'revenue-impact',
      eyebrow: 'Estrategia omnicanal',
      titleMain: 'Invierte en canales que',
      titleOutline: 'generan ingresos',
      description:
        'Te ayudamos a priorizar el mix correcto entre búsqueda, social, video, remarketing y automatización para maximizar resultados.',
      ctaText: 'Solicitar propuesta',
      ctaUrl: 'https://wa.me/5215616635041?text=Hola%2C%20quiero%20una%20propuesta%20de%20servicios%20omnicanal%20con%20DINGO',
      chartFootnote: 'Ilustración de prioridad por canal; en tu diagnóstico trabajamos con tus datos y objetivos reales.',
      orbGradient: 'radial-gradient(circle at 34% 24%, rgba(204, 255, 159, 0.96) 0%, rgba(142, 255, 47, 0.92) 45%, rgba(93, 227, 0, 0.82) 72%, rgba(47, 141, 0, 0.3) 100%)',
      visual: 'assets/images/banners/home/hero-banner2.jpg',
      visualAlt: 'Campañas y performance para vender más'
    }
  ];

  readonly approachSteps: ApproachStepItem[] = [
    {
      id: 'discover',
      title: 'Diagnóstico',
      description: 'Analizamos contexto, canales, audiencias y oportunidades reales para detectar el mayor potencial de crecimiento.',
      outcome: '',
      icon: 'bi-search'
    },
    {
      id: 'strategy',
      title: 'Estrategia',
      description: 'Diseñamos un sistema omnicanal con prioridades claras, objetivos medibles y enfoque comercial.',
      outcome: '',
      icon: 'bi-diagram-3'
    },
    {
      id: 'execution',
      title: 'Ejecución',
      description: 'Activamos campañas, contenido, automatización y tecnología con seguimiento continuo.',
      outcome: '',
      icon: 'bi-lightning-charge'
    },
    {
      id: 'optimization',
      title: 'Optimización',
      description: 'Medimos desempeño y optimizamos continuamente para mejorar conversión y rentabilidad.',
      outcome: '',
      icon: 'bi-graph-up-arrow'
    }
  ];

  readonly portfolioCases: PortfolioCaseItem[] = [
    {
      title: 'Liverpool',
      industry: 'Retail',
      metric: 'Digital Fidelity Strategy',
      description: 'Estrategia digital para fidelización, comunicación y crecimiento comercial.',
      image: 'assets/images/projects/portfolio/liverpool.jpg',
      link: '/clientes/liverpool'
    },
    {
      title: 'Carajillo',
      industry: 'Food & Beverage',
      metric: 'Marketing Strategy',
      description: 'Planeación de marketing para posicionar experiencias gastronómicas y acelerar demanda.',
      image: 'assets/images/projects/portfolio/carajillo.jpg',
      link: '/clientes/carajillo'
    },
    {
      title: 'Citibanamex',
      industry: 'Retail financiero',
      metric: 'Retail Marketing Strategy',
      description: 'Estrategia retail para campañas, comunicación y activaciones orientadas a negocio.',
      image: 'assets/images/projects/portfolio/citibanamex.jpg',
      link: '/clientes/citibanamex'
    },
    {
      title: 'Pernod Ricard',
      industry: 'Casino',
      metric: 'Marketing Strategy',
      description: 'Presencia y conexión comercial para marcas de consumo.',
      image: 'assets/images/projects/portfolio/pernod-ricard.png',
      link: '/clientes/pernod-ricard'
    },
    {
      title: 'Jaloma',
      industry: 'Retail',
      metric: 'Retail Marketing Strategy',
      description: 'Visibilidad, canales y comunicación de producto para retail.',
      image: 'assets/images/projects/portfolio/jaloma.png',
      link: '/clientes/jaloma'
    },
    {
      title: 'Football Legends',
      industry: 'Entretenimiento',
      metric: 'Marketing Business Development',
      description: 'Desarrollo comercial, marketing y presencia de marca.',
      image: 'assets/images/projects/portfolio/football-legends.png',
      link: '/clientes/football-legends'
    },
    {
      title: 'Organizer Star/HBO',
      industry: 'Streaming',
      metric: 'Streaming Website',
      description: 'Experiencia digital orientada a contenido y consumo en streaming.',
      image: 'assets/images/projects/portfolio/organizer-star.png',
      link: '/clientes/organizer-star'
    },
    {
      title: 'Estrella Real',
      industry: 'Lanzamiento',
      metric: 'Launching Marketing Strategy',
      description: 'Estrategia de lanzamiento para introducir marca, generar awareness y activar demanda.',
      image: 'assets/images/projects/portfolio/estrella-real.png',
      link: '/clientes/estrella-real'
    },
    {
      title: 'La Texana',
      industry: 'Retail',
      metric: 'Retail Marketing Strategy',
      description: 'Comunicación, presencia comercial y posicionamiento retail.',
      image: 'assets/images/projects/portfolio/la-texana.png',
      link: '/clientes/la-texana'
    },
    {
      title: 'Metaown',
      industry: 'Producto digital',
      metric: 'App Development e Integración AI',
      description: 'Producto digital con app e integración de inteligencia artificial.',
      image: 'assets/images/projects/portfolio/metaown.png',
      link: '/clientes/metaown'
    },
    {
      title: 'Evry',
      industry: 'Ecommerce',
      metric: 'Ecommerce Development',
      description: 'Ecommerce para experiencia de compra, operación digital y conversión.',
      image: 'assets/images/projects/portfolio/evry.png',
      link: '/clientes/evry'
    },
    {
      title: 'Pixel Mining',
      industry: 'Apps',
      metric: 'App Development',
      description: 'Aplicación digital con enfoque en producto, experiencia y escalabilidad.',
      image: 'assets/images/projects/portfolio/pixel-mining.png',
      link: '/clientes/pixel-mining'
    }
  ];

  get featuredPortfolioCases(): PortfolioCaseItem[] {
    return this.portfolioCases.slice(0, 5);
  }

  readonly calendlyPlaceholderUrl = 'https://calendly.com/tu-equipo/diagnostico-dingo';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private injector: Injector
  ) {}

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      document.body.classList.remove('mobile-menu-open');
      document.documentElement.classList.remove('mobile-menu-open');
      void this.setupPresentationExperience();
      this.scheduleDeferredChartSetup();
      runAfterHydration(this.injector, () => {
        this.scheduleDeferredBelowFoldSetup();
      });
    }
  }

  ngOnDestroy(): void {
    this.statsObserver?.disconnect();
    this.revealObserver?.disconnect();
    this.presentationScrollTrigger?.kill?.();
    this.presentationTransitionTl?.kill?.();
    this.presentationTransitionTl = null;
    this.presentationChromeTl?.kill?.();
    this.presentationChromeTl = null;
    if (this.counterAnimationFrame !== null) {
      cancelAnimationFrame(this.counterAnimationFrame);
      this.counterAnimationFrame = null;
    }
    this.hasAnimatedPresentationBullets = false;
    this.hasAnimatedPresentationSideUi = false;
    if (this.heroIntroReadyTimer !== null) {
      clearTimeout(this.heroIntroReadyTimer);
      this.heroIntroReadyTimer = null;
    }
    if (this.deferredChartsTimer !== null) {
      clearTimeout(this.deferredChartsTimer);
      this.deferredChartsTimer = null;
    }
    this.removeDeferredChartHeroReadyListener();
    if (this.deferredBelowFoldTimer !== null) {
      clearTimeout(this.deferredBelowFoldTimer);
      this.deferredBelowFoldTimer = null;
    }
    this.hasDispatchedHeroIntroReady = false;
    this.marketValueChart?.destroy?.();
    this.marketValueChart = null;
    this.audienceBarCharts?.age?.destroy?.();
    this.audienceBarCharts?.gender?.destroy?.();
    this.audienceBarCharts = null;
    this.impactBarChart?.destroy?.();
    this.impactBarChart = null;
    this.gsapCleanups.forEach((cleanup) => cleanup());
    this.gsapCleanups = [];
    this.chartResizeObserver?.disconnect();
    this.chartResizeObserver = null;
  }

  private scheduleDeferredChartSetup(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const setup = () => {
      if (!isPlatformBrowser(this.platformId)) return;
      this.deferredChartsTimer = null;
      this.removeDeferredChartHeroReadyListener();
      void Promise.all([
        this.setupPresentationChart(),
        this.setupAudienceDualCharts(),
        this.setupImpactBarChart()
      ]).then(() => {
        this.setupChartsResizeObserver();
        this.refreshPresentationScrollTrigger();
      });
    };

    const queueSetup = () => {
      const win = window as Window & {
        requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
      };

      if (typeof win.requestIdleCallback === 'function') {
        win.requestIdleCallback(setup, { timeout: 2200 });
        return;
      }

      this.deferredChartsTimer = setTimeout(setup, 700);
    };

    if (this.hasDispatchedHeroIntroReady) {
      queueSetup();
      return;
    }

    this.deferredChartHeroReadyListener = () => {
      this.removeDeferredChartHeroReadyListener();
      if (this.deferredChartsTimer !== null) {
        clearTimeout(this.deferredChartsTimer);
        this.deferredChartsTimer = null;
      }
      queueSetup();
    };

    window.addEventListener('dingo:hero-intro-complete', this.deferredChartHeroReadyListener, { once: true });

    this.deferredChartsTimer = setTimeout(() => {
      this.removeDeferredChartHeroReadyListener();
      queueSetup();
    }, 5200);
  }

  private removeDeferredChartHeroReadyListener(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.deferredChartHeroReadyListener) return;
    window.removeEventListener('dingo:hero-intro-complete', this.deferredChartHeroReadyListener);
    this.deferredChartHeroReadyListener = null;
  }

  private scheduleDeferredBelowFoldSetup(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    void this.setupApproachTimelineAnimation();

    this.deferredBelowFoldTimer = setTimeout(() => {
      this.deferredBelowFoldTimer = null;
      this.setupLogoSwiper();
      void this.setupStatsCounterAnimation();
      void this.setupGlobalScrollRevealAnimation();
    }, 120);
  }

  private isApproachItemInView(item: HTMLElement, threshold = 0.92): boolean {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return false;

    const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
    if (!viewportHeight) return false;

    const rect = item.getBoundingClientRect();
    return rect.top <= viewportHeight * threshold && rect.bottom > 12;
  }

  private syncApproachTimelineVisibleState(
    gsap: typeof import('gsap').gsap,
    items: HTMLElement[],
    cards: HTMLElement[],
    dots: HTMLElement[],
    icons: Array<HTMLElement | undefined>,
    stepNumbers: Array<HTMLElement | undefined>,
    setActiveStep: (activeIndex: number) => void
  ): void {
    let activeIndex = 0;
    let bestTop = Number.NEGATIVE_INFINITY;

    items.forEach((item, index) => {
      if (!this.isApproachItemInView(item, 0.96)) return;

      const rect = item.getBoundingClientRect();
      if (rect.top >= bestTop) {
        bestTop = rect.top;
        activeIndex = index;
      }

      const card = cards[index];
      const dot = dots[index];
      const icon = icons[index];
      const stepNumber = stepNumbers[index];

      if (card) {
        gsap.set(card, { autoAlpha: 1, y: 0, x: 0, scale: 1 });
      }
      if (dot) {
        gsap.set(dot, { autoAlpha: 1, scale: 1 });
      }
      if (stepNumber) {
        gsap.set(stepNumber, { autoAlpha: 1, y: 0 });
      }
      if (icon) {
        gsap.set(icon, { rotate: 0, scale: 1 });
      }
    });

    if (bestTop > Number.NEGATIVE_INFINITY) {
      setActiveStep(activeIndex);
    }
  }

  private async setupStatsCounterAnimation(): Promise<void> {
    const statsEl = this.statsBlock?.nativeElement;
    if (!statsEl) return;

    this.resetCounters();

    let hasAnimated = false;
    const runCounter = () => {
      if (hasAnimated) return;
      hasAnimated = true;
      this.animateCounters();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          runCounter();
          observer.disconnect();
        });
      },
      {
        root: null,
        rootMargin: '0px 0px -18% 0px',
        threshold: 0.15
      }
    );

    observer.observe(statsEl);
    this.gsapCleanups.push(() => observer.disconnect());

    requestAnimationFrame(() => {
      const rect = statsEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      if (rect.top <= viewportHeight * 0.82 && rect.bottom > 0) {
        runCounter();
      }
    });

    window.setTimeout(() => {
      const rect = statsEl.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      if (!hasAnimated && rect.top <= viewportHeight * 0.95 && rect.bottom > 0) {
        runCounter();
      }
    }, 1800);
  }

  private async setupGlobalScrollRevealAnimation(): Promise<void> {
    this.prepareAutoRevealElements();
    const root = this.homeRoot?.nativeElement;
    if (!root) return;

    await initPageScrollReveal(root, this.gsapCleanups, {
      excludeClosest: ['.presentation-slider', '.approach-timeline']
    });
  }

  private prepareAutoRevealElements(): void {
    const root = this.homeRoot?.nativeElement;
    if (!root) return;

    const revealGroups: Array<{ selector: string; type: string; step: number }> = [
      { selector: '.home-trust-carousel .trust-carousel-shell', type: 'card', step: 0 },
      { selector: '.home-trust-carousel .trust-control-btn', type: 'button', step: 1 },
      { selector: '.home-trust-carousel .trust-logo-item', type: 'logo', step: 0.08 },
      { selector: '.section-head:not([data-reveal]), .home-section-head:not([data-reveal])', type: 'section-head', step: 0 },
      { selector: '.home-metrics-grid:not([data-reveal])', type: 'card', step: 0 },
      { selector: '.testimonial-neon-card:not([data-reveal])', type: 'card', step: 0 },
      { selector: '.services-device:not([data-reveal])', type: 'media', step: 2 },
      { selector: '.service-node:not([data-reveal])', type: 'card', step: 0.8 },
      { selector: '.portfolio-section-snapshot:not([data-reveal])', type: 'card', step: 2 },
      { selector: '.case-card:not([data-reveal])', type: 'card', step: 0.55 },
      { selector: '.home-final-cta-card:not([data-reveal])', type: 'card', step: 0 }
    ];

    revealGroups.forEach(({ selector, type, step }) => {
      const elements = Array.from(root.querySelectorAll<HTMLElement>(selector));
      elements.forEach((element, index) => {
        if (element.closest('.presentation-slider')) return;
        if (element.closest('.approach-timeline')) return;
        if (element.closest('.reveal-on-scroll') && !element.classList.contains('reveal-on-scroll')) return;
        if (element.parentElement?.closest('.reveal-on-scroll, .contact-reveal, .client-reveal, .footer-reveal')) {
          return;
        }

        element.classList.add('reveal-on-scroll');
        if (!element.dataset['reveal']) {
          element.dataset['reveal'] = type;
        }
        if (!element.style.getPropertyValue('--reveal-order')) {
          const order = Math.min(8, Number((index * step).toFixed(2)));
          element.style.setProperty('--reveal-order', String(order));
        }
      });
    });
  }

  presentationStepLabel(index: number): string {
    return String(Math.max(0, index + 1)).padStart(2, '0');
  }

  private getPresentationIndexFromProgress(progress: number): number {
    const total = this.presentationSlides.length;
    if (total <= 1) return 0;

    const clampedProgress = Math.min(0.999999, Math.max(0, progress));
    return Math.min(total - 1, Math.max(0, Math.floor(clampedProgress * total)));
  }

  private isPresentationMobileViewport(): boolean {
    return (
      isPlatformBrowser(this.platformId) &&
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767.98px)').matches
    );
  }

  private getPresentationSlidePointerEvents(isInteractive: boolean): 'auto' | 'none' {
    if (this.isPresentationMobileViewport()) {
      return 'none';
    }

    return isInteractive ? 'auto' : 'none';
  }

  private getPresentationProgressForIndex(index: number): number {
    const total = this.presentationSlides.length;
    if (total <= 1) return 0;

    return Math.min(1, Math.max(0, index / total));
  }

  private holdPresentationProgrammaticTarget(index: number): void {
    this.presentationProgrammaticTargetIndex = index;

    if (this.presentationProgrammaticTargetTimer !== null) {
      clearTimeout(this.presentationProgrammaticTargetTimer);
    }

    this.presentationProgrammaticTargetTimer = window.setTimeout(() => {
      this.clearPresentationProgrammaticTarget();
      this.presentationScrollTrigger?.update?.();
    }, 1050);
  }

  private clearPresentationProgrammaticTarget(): void {
    this.presentationProgrammaticTargetIndex = null;

    if (this.presentationProgrammaticTargetTimer !== null) {
      clearTimeout(this.presentationProgrammaticTargetTimer);
      this.presentationProgrammaticTargetTimer = null;
    }
  }

  goToPresentationSlide(index: number): void {
    const total = this.presentationSlides.length;
    if (!total) return;
    const normalized = ((index % total) + total) % total;
    const isMobileViewport = this.isPresentationMobileViewport();

    if (this.presentationScrollTrigger && isPlatformBrowser(this.platformId)) {
      const start = Number(this.presentationScrollTrigger.start ?? 0);
      const end = Number(this.presentationScrollTrigger.end ?? start);
      const progress = this.getPresentationProgressForIndex(normalized);
      const targetScroll = start + (end - start) * progress;

      if (isMobileViewport) {
        this.holdPresentationProgrammaticTarget(normalized);
      }

      window.scrollTo({ top: targetScroll, behavior: 'smooth' });

      if (isMobileViewport) {
        this.setPresentationSlide(normalized, true);
        window.setTimeout(() => {
          this.presentationScrollTrigger?.update?.();
        }, 90);
      }
    } else {
      this.setPresentationSlide(normalized, true);
    }
  }

  private async setupPresentationExperience(): Promise<void> {
    const sectionEl = this.chartsPresentation?.nativeElement;
    if (!sectionEl) return;

    const [{ gsap }, { ScrollTrigger }] = await Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger')
    ]);

    this.gsapLib = gsap;
    gsap.registerPlugin(ScrollTrigger);

    const ctx = gsap.context(() => {
      const reduceMotion =
        isPlatformBrowser(this.platformId) &&
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.presentationSlideEls = Array.from(sectionEl.querySelectorAll<HTMLElement>('.presentation-slide'));
      if (!this.presentationSlideEls.length) return;

      gsap.set(this.presentationSlideEls, {
        autoAlpha: 0,
        y: 0,
        scale: 1.035,
        clipPath: 'inset(8% 0% 8% 0%)',
        pointerEvents: this.getPresentationSlidePointerEvents(false)
      });
      gsap.set(this.presentationSlideEls[0], {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        clipPath: 'inset(0% 0% 0% 0%)',
        pointerEvents: this.getPresentationSlidePointerEvents(true)
      });
      this.normalizePresentationSlides(0);
      this.activePresentationIndex = 0;
      void this.waitForInitialPresentationMedia(sectionEl).then(() => {
        if (!this.gsapLib || this.activePresentationIndex !== 0 || !this.presentationSlideEls.length) return;
        this.animatePresentationInternals(0);
      });
      this.animatePresentationChrome(true);

      if (reduceMotion || this.presentationSlides.length <= 1) {
        this.animatePresentationChrome(false);
        this.scheduleHeroIntroReady(120);
        return;
      }

      this.syncPresentationViewportHeight(sectionEl);

      this.presentationScrollTrigger = ScrollTrigger.create({
        trigger: sectionEl,
        start: 'top top',
        end: 'bottom bottom',
        snap: {
          snapTo: 1 / this.presentationSlides.length,
          duration: { min: 0.2, max: 0.58 },
          ease: 'power2.inOut',
        },
        invalidateOnRefresh: true,
        onRefresh: (self: any) => {
          if (this.presentationProgrammaticTargetIndex !== null) {
            this.normalizePresentationSlides(this.presentationProgrammaticTargetIndex);
            return;
          }

          const normalizedIndex = this.getPresentationIndexFromProgress(self.progress);
          this.activePresentationIndex = normalizedIndex;
          this.normalizePresentationSlides(normalizedIndex);
        },
        onUpdate: (self: any) => {
          const total = this.presentationSlides.length;
          if (total <= 1) return;
          const nextIndex = this.getPresentationIndexFromProgress(self.progress);

          if (this.presentationProgrammaticTargetIndex !== null) {
            const targetProgress = this.getPresentationProgressForIndex(this.presentationProgrammaticTargetIndex);
            const isAtTarget = Math.abs(Number(self.progress ?? 0) - targetProgress) < 0.018;

            if (nextIndex !== this.presentationProgrammaticTargetIndex && !isAtTarget) {
              return;
            }

            this.clearPresentationProgrammaticTarget();
          }

          if (nextIndex !== this.activePresentationIndex) {
            this.setPresentationSlide(nextIndex, true);
          }
        }
      });
      this.setupPresentationViewportSync(sectionEl, ScrollTrigger);
      this.schedulePresentationScrollRefresh(ScrollTrigger);
    }, sectionEl);

    this.gsapCleanups.push(() => {
      this.presentationScrollTrigger?.kill?.();
      this.presentationScrollTrigger = null;
      this.presentationTransitionTl?.kill?.();
      this.presentationTransitionTl = null;
      this.presentationChromeTl?.kill?.();
      this.presentationChromeTl = null;
      this.clearPresentationProgrammaticTarget();
      this.hasAnimatedPresentationBullets = false;
      this.hasAnimatedPresentationSideUi = false;
      if (this.heroIntroReadyTimer !== null) {
        clearTimeout(this.heroIntroReadyTimer);
        this.heroIntroReadyTimer = null;
      }
      this.hasDispatchedHeroIntroReady = false;
      ctx.revert();
    });
  }

  private async waitForInitialPresentationMedia(sectionEl: HTMLElement): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    const firstSlide = sectionEl.querySelector<HTMLElement>('.presentation-slide');
    const images = firstSlide
      ? Array.from(firstSlide.querySelectorAll<HTMLImageElement>('img')).filter((image) => !image.complete)
      : [];

    const imagePromises = images.map((image) => {
      if (typeof image.decode === 'function') {
        return image.decode().catch(() => undefined);
      }

      return new Promise<void>((resolve) => {
        image.addEventListener('load', () => resolve(), { once: true });
        image.addEventListener('error', () => resolve(), { once: true });
      });
    });

    const fontReady =
      typeof document !== 'undefined' && 'fonts' in document
        ? (document as Document & { fonts: FontFaceSet }).fonts.ready.catch(() => undefined)
        : Promise.resolve();

    await Promise.race([
      Promise.all([...imagePromises, fontReady]).then(() => undefined),
      new Promise<void>((resolve) => window.setTimeout(resolve, 900))
    ]);

    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));
  }

  private scheduleHeroIntroReady(delayMs: number): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (typeof window === 'undefined') return;
    if (this.hasDispatchedHeroIntroReady) return;

    if (this.heroIntroReadyTimer !== null) {
      clearTimeout(this.heroIntroReadyTimer);
      this.heroIntroReadyTimer = null;
    }

    this.heroIntroReadyTimer = setTimeout(() => {
      if (this.hasDispatchedHeroIntroReady) return;
      this.hasDispatchedHeroIntroReady = true;
      window.dispatchEvent(new CustomEvent('dingo:hero-intro-complete'));
      this.heroIntroReadyTimer = null;
    }, Math.max(0, delayMs));
  }

  private initChartRevealState(chart: any, full: boolean): void {
    chart.$dingoReveal = {
      gridProgress: full ? 1 : 0,
      datasetsEnabled: full,
      dataProgress: full ? 1 : 0,
      barFillProgress: full ? 1 : 0,
      clipDatasets: false,
      barFillEnabled: false,
      movingPointEnabled: false
    };
  }

  private prepareBarChartInitialState(chart: any): void {
    if (!chart) return;
    if (!chart.$dingoOriginalDatasetData) {
      chart.$dingoOriginalDatasetData = chart.data.datasets.map((dataset: any) => [...dataset.data]);
    }
    chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
      dataset.data = chart.$dingoOriginalDatasetData[datasetIndex].map(() => 0);
    });
    chart.$dingoReveal = {
      gridProgress: 0,
      datasetsEnabled: false,
      dataProgress: 0,
      barFillProgress: 0,
      clipDatasets: false,
      barFillEnabled: false,
      movingPointEnabled: false
    };
    if (chart.options?.plugins?.legend) {
      chart.options.plugins.legend.display = true;
    }
    chart.update('none');
  }

  private runChartGridThenData(
    chart: any,
    hasLegend = false,
    dataDelayMs = 120,
    mode: 'line-sweep' | 'bar-fill' = 'bar-fill'
  ): void {
    if (!chart || !isPlatformBrowser(this.platformId)) return;
    const clipDatasets = mode === 'line-sweep';
    const movingPointEnabled = mode === 'line-sweep';
    const barFillEnabled = false;
    const isBarFill = mode === 'bar-fill';
    if (isBarFill && !chart.$dingoOriginalDatasetData) {
      chart.$dingoOriginalDatasetData = chart.data.datasets.map((dataset: any) => [...dataset.data]);
    }
    const originalBarData = isBarFill ? chart.$dingoOriginalDatasetData as Array<Array<number>> : null;

    if (typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      if (originalBarData) {
        chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
          dataset.data = [...originalBarData[datasetIndex]];
        });
      }
      chart.$dingoReveal = {
        gridProgress: 1,
        datasetsEnabled: true,
        dataProgress: 1,
        barFillProgress: 1,
        clipDatasets,
        barFillEnabled,
        movingPointEnabled
      };
      if (hasLegend && chart.options?.plugins?.legend) {
        chart.options.plugins.legend.display = true;
      }
      chart.update();
      return;
    }

    chart.stop?.();
    if (!chart.$dingoReveal) {
      chart.$dingoReveal = {};
    }
    const st = chart.$dingoReveal;
    st.gridProgress = 0;
    st.datasetsEnabled = false;
    st.dataProgress = 0;
    st.barFillProgress = 0;
    st.clipDatasets = clipDatasets;
    st.barFillEnabled = barFillEnabled;
    st.movingPointEnabled = movingPointEnabled;
    if (hasLegend && !isBarFill && chart.options?.plugins?.legend) {
      chart.options.plugins.legend.display = false;
    }
    if (originalBarData) {
      chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
        dataset.data = originalBarData[datasetIndex].map(() => 0);
      });
    }
    chart.update('none');

    const start = performance.now();
    const ms = this.chartGridRevealMs;

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / ms);
      st.gridProgress = t * t * (3 - 2 * t);
      chart.draw();
      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        st.gridProgress = 1;
        window.setTimeout(() => {
          st.datasetsEnabled = true;
          st.dataProgress = 0;

          if (mode === 'bar-fill') {
            st.barFillProgress = 1;
            st.dataProgress = 1;
            st.barFillEnabled = false;
            if (originalBarData) {
              chart.data.datasets.forEach((dataset: any, datasetIndex: number) => {
                dataset.data = [...originalBarData[datasetIndex]];
              });
            }
            chart.update();
            if (hasLegend && !isBarFill && chart.options?.plugins?.legend) {
              window.setTimeout(() => {
                chart.options.plugins.legend.display = true;
                chart.update('none');
              }, Math.round(this.chartDataAnimMs * 0.82));
            }
            return;
          }

          chart.update();
          const dataStart = performance.now();
          const dataMs = this.chartDataAnimMs;
          const dataStep = (dataNow: number) => {
            const dataT = Math.min(1, (dataNow - dataStart) / dataMs);
            st.dataProgress = this.easeOutCubic(dataT);
            chart.draw();
            if (dataT < 1) {
              requestAnimationFrame(dataStep);
            } else {
              st.dataProgress = 1;
              if (hasLegend && chart.options?.plugins?.legend) {
                chart.options.plugins.legend.display = true;
              }
              chart.update();
            }
          };

          requestAnimationFrame(dataStep);
        }, Math.max(0, dataDelayMs));
      }
    };

    requestAnimationFrame(step);
  }

  private setupChartsResizeObserver(): void {
    if (!isPlatformBrowser(this.platformId) || typeof ResizeObserver === 'undefined') return;
    const el = this.chartsPresentation?.nativeElement;
    if (!el) return;
    this.chartResizeObserver?.disconnect();
    this.chartResizeObserver = new ResizeObserver(() => {
      queueMicrotask(() => {
        this.marketValueChart?.resize?.();
        this.audienceBarCharts?.age?.resize?.();
        this.audienceBarCharts?.gender?.resize?.();
        this.impactBarChart?.resize?.();
      });
    });
    this.chartResizeObserver.observe(el);
  }

  private schedulePresentationScrollRefresh(ScrollTrigger: { refresh: (safe?: boolean) => void }): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return;

    const refresh = () => this.refreshPresentationScrollTrigger(ScrollTrigger);
    const onLoad = () => refresh();

    requestAnimationFrame(() => {
      requestAnimationFrame(refresh);
    });
    window.setTimeout(refresh, 240);
    window.setTimeout(refresh, 900);

    if (document.readyState === 'complete') {
      window.setTimeout(refresh, 80);
    } else {
      window.addEventListener('load', onLoad, { once: true });
      this.gsapCleanups.push(() => window.removeEventListener('load', onLoad));
    }
  }

  private async refreshPresentationScrollTrigger(ScrollTriggerRef?: { refresh: (safe?: boolean) => void }): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const ScrollTrigger =
        ScrollTriggerRef ?? (await import('gsap/ScrollTrigger')).ScrollTrigger;
      ScrollTrigger.refresh(true);
      this.presentationScrollTrigger?.update?.();
    } catch {
      // ScrollTrigger can be busy while mobile browser chrome is settling; the next scheduled refresh will retry.
    }
  }

  private async refreshAllScrollTriggers(ScrollTriggerRef?: { refresh: (safe?: boolean) => void }): Promise<void> {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const ScrollTrigger =
        ScrollTriggerRef ?? (await import('gsap/ScrollTrigger')).ScrollTrigger;
      ScrollTrigger.refresh(true);
      this.presentationScrollTrigger?.update?.();
    } catch {
      requestAnimationFrame(() => {
        void this.refreshAllScrollTriggers(ScrollTriggerRef);
      });
    }
  }

  private getPresentationViewportHeight(): number {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return 0;
    // Use a stable viewport source; visualViewport can fluctuate while browser chrome collapses.
    return Math.round(window.innerHeight || document.documentElement.clientHeight || 0);
  }

  private syncPresentationViewportHeight(sectionEl: HTMLElement): void {
    const viewportHeight = this.getPresentationViewportHeight();
    if (!viewportHeight) return;

    sectionEl.style.setProperty('--presentation-mobile-vh', `${viewportHeight}px`);
    sectionEl.style.setProperty(
      '--presentation-scroll-height',
      `${viewportHeight * Math.max(1, this.presentationSlides.length + 1)}px`
    );
  }

  private setupPresentationViewportSync(
    sectionEl: HTMLElement,
    ScrollTrigger: { refresh: (safe?: boolean) => void }
  ): void {
    if (!isPlatformBrowser(this.platformId) || typeof window === 'undefined') return;

    let rafId: number | null = null;
    const scheduleSync = () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        this.syncPresentationViewportHeight(sectionEl);
        void this.refreshPresentationScrollTrigger(ScrollTrigger);
      });
    };

    window.addEventListener('resize', scheduleSync);
    window.addEventListener('orientationchange', scheduleSync);

    this.gsapCleanups.push(() => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      window.removeEventListener('resize', scheduleSync);
      window.removeEventListener('orientationchange', scheduleSync);
    });
  }

  private replayMarketChartIntro(): void {
    this.runChartGridThenData(this.marketValueChart, false, 140, 'line-sweep');
  }

  private replayAudienceChartsIntro(): void {
    this.runChartGridThenData(this.audienceBarCharts?.age, false, 90, 'bar-fill');
    window.setTimeout(() => {
      this.runChartGridThenData(this.audienceBarCharts?.gender, true, 90, 'bar-fill');
    }, 180);
  }

  private replayImpactBarChartIntro(): void {
    this.runChartGridThenData(this.impactBarChart, false, 100, 'bar-fill');
  }

  private async setupPresentationChart(): Promise<void> {
    const sectionEl = this.chartsPresentation?.nativeElement;
    if (!sectionEl) return;

    const canvas = sectionEl.querySelector<HTMLCanvasElement>('[data-market-chart]');
    if (!canvas) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    const { default: Chart } = await import('chart.js/auto');
    registerDingoChartRevealPlugin(Chart);
    this.marketValueChart?.destroy?.();

    const areaGradient = context.createLinearGradient(0, 0, 0, 360);
    areaGradient.addColorStop(0, 'rgba(116, 252, 3, 0.46)');
    areaGradient.addColorStop(0.6, 'rgba(116, 252, 3, 0.15)');
    areaGradient.addColorStop(1, 'rgba(116, 252, 3, 0.02)');

    const dataDuration = this.chartDataAnimMs;

    this.marketValueChart = new Chart(context, {
      type: 'line',
      data: {
        labels: ['Diagnóstico', 'Mes 1', 'Mes 2', 'Mes 3', 'Mes 4', 'Mes 5', 'Mes 6'],
        datasets: [
          {
            data: [1.1, 1.4, 1.9, 2.3, 2.8, 3.4, 4.2],
            borderColor: 'rgba(142, 255, 47, 0.95)',
            borderWidth: 2.4,
            fill: true,
            backgroundColor: areaGradient,
            tension: 0.43,
            pointRadius: 0,
            pointBorderWidth: [0, 0, 0, 0, 0, 0, 0, 0, 0],
            pointHoverRadius: 0,
            pointBackgroundColor: 'transparent'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: dataDuration,
          easing: 'easeOutQuart'
        },
        animations: {
          y: {
            type: 'number',
            properties: ['y'],
            easing: 'easeOutCubic',
            duration: dataDuration,
            from: (ctx: any) => {
              if (ctx.type === 'point') {
                return ctx.chart.scales.y.getPixelForValue(0);
              }
            }
          }
        },
        layout: {
          padding: { top: 2, right: 6, bottom: 2, left: 4 }
        },
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            intersect: false,
            displayColors: false,
            backgroundColor: 'rgba(7, 16, 9, 0.94)',
            borderColor: 'rgba(116, 252, 3, 0.45)',
            borderWidth: 1,
            titleColor: '#ffffff',
            bodyColor: 'rgba(255, 255, 255, 0.88)',
            padding: 10,
            callbacks: {
              label: (tooltipContext: any) => `${Number(tooltipContext.parsed.y).toFixed(1)}x ROAS estimado`
            }
          }
        },
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: 'rgba(255, 255, 255, 0.07)',
              drawTicks: false
            },
            ticks: {
              color: 'rgba(255, 255, 255, 0.5)',
              font: {
                size: 10,
                family: 'Poppins, sans-serif'
              }
            },
            border: {
              display: false
            }
          },
          y: {
            min: 0,
            max: 5,
            ticks: {
              stepSize: 1,
              color: 'rgba(255, 255, 255, 0.5)',
              font: {
                size: 10,
                family: 'Poppins, sans-serif'
              },
              callback: (value: string | number) => `${value}x`
            },
            grid: {
              color: 'rgba(255, 255, 255, 0.09)',
              drawTicks: false
            },
            border: {
              display: false
            }
          }
        }
      }
    });

    this.initChartRevealState(this.marketValueChart, true);
    this.marketValueChart.update();
  }

  private async setupAudienceDualCharts(): Promise<void> {
    const sectionEl = this.chartsPresentation?.nativeElement;
    if (!sectionEl) return;

    const ageCanvas = sectionEl.querySelector<HTMLCanvasElement>('[data-audience-age-chart]');
    const genderCanvas = sectionEl.querySelector<HTMLCanvasElement>('[data-audience-gender-chart]');
    if (!ageCanvas || !genderCanvas) return;

    const ageCtx = ageCanvas.getContext('2d');
    const genderCtx = genderCanvas.getContext('2d');
    if (!ageCtx || !genderCtx) return;

    const { default: Chart } = await import('chart.js/auto');
    registerDingoChartRevealPlugin(Chart);
    this.audienceBarCharts?.age?.destroy?.();
    this.audienceBarCharts?.gender?.destroy?.();

    const commonFont = { family: 'Poppins, sans-serif', size: 10 };
    const tickColor = 'rgba(255, 255, 255, 0.52)';
    const gridColor = 'rgba(255, 255, 255, 0.09)';
    const barPrimary = 'rgba(142, 255, 47, 0.9)';
    const barSecondary = 'rgba(79, 176, 1, 0.78)';
    const tooltipBase = {
      backgroundColor: 'rgba(7, 16, 9, 0.94)',
      borderColor: 'rgba(116, 252, 3, 0.45)',
      borderWidth: 1,
      titleColor: '#ffffff',
      bodyColor: 'rgba(255, 255, 255, 0.88)',
      padding: 10,
    };

    const barAnimDuration = this.chartDataAnimMs;

    const ageChart = new Chart(ageCtx, {
      type: 'bar',
      data: {
        labels: ['Visitas', 'Prospectos', 'Leads', 'Cotizaciones', 'Clientes'],
        datasets: [
          {
            label: 'Avance del embudo',
            data: [92, 74, 56, 38, 24],
            backgroundColor: barPrimary,
            borderColor: 'rgba(116, 252, 3, 0.35)',
            borderWidth: 1,
            borderRadius: 4,
            barPercentage: 0.65,
            categoryPercentage: 0.78,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: barAnimDuration,
          easing: 'easeOutQuart',
          delay: (ctx: any) => (ctx.type === 'bar' ? (ctx.dataIndex ?? 0) * 42 : 0),
        },
        animations: {
          y: {
            type: 'number',
            properties: ['y'],
            easing: 'easeOutCubic',
            duration: barAnimDuration,
            from: (ctx: any) => {
              if (ctx.type === 'bar') {
                return ctx.chart.scales.y.getPixelForValue(0);
              }
            },
          },
        },
        layout: {
          padding: { top: 4, right: 4, bottom: 4, left: 4 },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            ...tooltipBase,
            displayColors: false,
            callbacks: {
              label: (ctx: any) => `${ctx.dataset.label}: ${ctx.parsed.y}%`,
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: 'rgba(255, 255, 255, 0.06)',
              drawTicks: false,
            },
            ticks: {
              color: tickColor,
              font: commonFont,
              maxRotation: 0,
              autoSkip: false,
            },
            border: { display: false },
          },
          y: {
            min: 0,
            max: 100,
            ticks: {
              stepSize: 20,
              color: tickColor,
              font: commonFont,
              callback: (value: string | number) => `${value}%`,
            },
            grid: { color: gridColor, drawTicks: false },
            border: { display: false },
          },
        },
      },
    });

    const genderChart = new Chart(genderCtx, {
      type: 'bar',
      data: {
        labels: ['Mes 1', 'Mes 2', 'Mes 3', 'Mes 4', 'Mes 5', 'Mes 6'],
        datasets: [
          {
            label: 'Clientes nuevos',
            data: [8, 14, 23, 35, 49, 67],
            backgroundColor: barPrimary,
            borderColor: 'rgba(116, 252, 3, 0.28)',
            borderWidth: 1,
            borderRadius: 3,
            barPercentage: 0.92,
            categoryPercentage: 0.72,
          },
          {
            label: 'Clientes recurrentes',
            data: [3, 6, 11, 18, 27, 39],
            backgroundColor: barSecondary,
            borderColor: 'rgba(79, 176, 1, 0.3)',
            borderWidth: 1,
            borderRadius: 3,
            barPercentage: 0.92,
            categoryPercentage: 0.72,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: barAnimDuration,
          easing: 'easeOutQuart',
          delay: (ctx: any) => (ctx.type === 'bar' ? (ctx.dataIndex ?? 0) * 38 : 0),
        },
        animations: {
          y: {
            type: 'number',
            properties: ['y'],
            easing: 'easeOutCubic',
            duration: barAnimDuration,
            from: (ctx: any) => {
              if (ctx.type === 'bar') {
                return ctx.chart.scales.y.getPixelForValue(0);
              }
            },
          },
        },
        layout: {
          padding: { top: 4, right: 4, bottom: 2, left: 4 },
        },
        plugins: {
          legend: {
            display: true,
            position: 'bottom',
            align: 'end',
            labels: {
              boxWidth: 8,
              boxHeight: 8,
              color: 'rgba(255, 255, 255, 0.72)',
              font: { ...commonFont, size: 9 },
              padding: 14,
              usePointStyle: true,
              pointStyle: 'rectRounded',
            },
          },
          tooltip: {
            ...tooltipBase,
              displayColors: true,
            callbacks: {
              label: (ctx: any) =>
                ctx.dataset.label ? `${ctx.dataset.label}: ${ctx.parsed.y}` : `${ctx.parsed.y}`,
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: true,
              color: 'rgba(255, 255, 255, 0.06)',
              drawTicks: false,
            },
            ticks: { color: tickColor, font: commonFont, maxRotation: 0 },
            border: { display: false },
          },
          y: {
            min: 0,
            max: 80,
            ticks: {
              stepSize: 16,
              color: tickColor,
              font: commonFont,
              callback: (value: string | number) => `${value}`,
            },
            grid: { color: gridColor, drawTicks: false },
            border: { display: false },
          },
        },
      },
    });

    this.audienceBarCharts = { age: ageChart, gender: genderChart };
    this.prepareBarChartInitialState(ageChart);
    this.prepareBarChartInitialState(genderChart);
  }

  private async setupImpactBarChart(): Promise<void> {
    const sectionEl = this.chartsPresentation?.nativeElement;
    if (!sectionEl) return;

    const canvas = sectionEl.querySelector<HTMLCanvasElement>('[data-impact-bar-chart]');
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { default: Chart } = await import('chart.js/auto');
    registerDingoChartRevealPlugin(Chart);
    this.impactBarChart?.destroy?.();

    const commonFont = { family: 'Poppins, sans-serif', size: 10 };
    const tickColor = 'rgba(255, 255, 255, 0.55)';
    const gridColor = 'rgba(255, 255, 255, 0.1)';
    const barAnimDuration = this.chartDataAnimMs;

    const barGradient = (chart: any) => {
      const { chartArea } = chart;
      if (!chartArea) return 'rgba(142, 255, 47, 0.85)';
      const g = chart.ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      g.addColorStop(0, 'rgba(45, 110, 12, 0.95)');
      g.addColorStop(0.45, 'rgba(79, 176, 1, 0.92)');
      g.addColorStop(0.78, 'rgba(116, 252, 3, 0.95)');
      g.addColorStop(1, 'rgba(196, 255, 132, 1)');
      return g;
    };

    this.impactBarChart = new Chart(ctx, {
      type: 'bar',
      data: {
        labels: ['Video', 'Social', 'Search', 'Display', 'Email', 'Influencers', 'SEO', 'CRM', 'Remarketing'],
        datasets: [
          {
            label: 'Prioridad por canal',
            data: [238, 210, 192, 168, 148, 128, 108, 88, 72],
            borderColor: 'rgba(116, 252, 3, 0.35)',
            borderWidth: 1,
            borderRadius: 5,
            barPercentage: 0.72,
            categoryPercentage: 0.82,
            backgroundColor: (c: any) => barGradient(c.chart),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: {
          duration: barAnimDuration,
          easing: 'easeOutQuart',
          delay: (c: any) => (c.type === 'bar' ? (c.dataIndex ?? 0) * 45 : 0),
        },
        animations: {
          y: {
            type: 'number',
            properties: ['y'],
            easing: 'easeOutCubic',
            duration: barAnimDuration,
            from: (c: any) => (c.type === 'bar' ? c.chart.scales.y.getPixelForValue(0) : undefined),
          },
        },
        layout: {
          padding: { top: 8, right: 6, bottom: 4, left: 4 },
        },
        plugins: {
          legend: { display: false },
          tooltip: {
            backgroundColor: 'rgba(7, 16, 9, 0.94)',
            borderColor: 'rgba(116, 252, 3, 0.45)',
            borderWidth: 1,
            titleColor: '#ffffff',
            bodyColor: 'rgba(255, 255, 255, 0.88)',
            padding: 10,
            displayColors: false,
            callbacks: {
              label: (tooltipContext: any) => `${tooltipContext.parsed.y} M`,
            },
          },
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: {
              color: tickColor,
              font: commonFont,
              maxRotation: 35,
              minRotation: 0,
              autoSkip: true,
            },
            border: { display: false },
          },
          y: {
            min: 0,
            max: 260,
            ticks: {
              stepSize: 52,
              color: tickColor,
              font: commonFont,
              callback: (value: string | number) => `${value}M`,
            },
            grid: { color: gridColor, drawTicks: false },
            border: { display: false },
          },
        },
      },
    });

    this.prepareBarChartInitialState(this.impactBarChart);
  }

  private setPresentationSlide(index: number, animateTransition: boolean): void {
    if (!this.gsapLib || !this.presentationSlideEls.length) {
      this.activePresentationIndex = index;
      return;
    }

    if (index < 0 || index >= this.presentationSlideEls.length) return;
    if (index === this.activePresentationIndex) {
      this.normalizePresentationSlides(index);
      return;
    }

    const gsap = this.gsapLib;
    const currentIndex = this.activePresentationIndex;
    const direction = index > currentIndex ? 1 : -1;
    const currentSlide = this.presentationSlideEls[currentIndex];
    const nextSlide = this.presentationSlideEls[index];
    const outDuration = animateTransition ? 0.92 : 0.52;
    const inDuration = animateTransition ? 1.18 : 0.74;
    const slideShift = animateTransition ? 54 : 30;
    const overlapStart = animateTransition ? outDuration * 0.46 : outDuration * 0.48;
    const internalsDelay = animateTransition ? overlapStart + 0.12 : 0.14;

    this.normalizePresentationSlides(currentIndex, index);
    gsap.set(currentSlide, {
      autoAlpha: 1,
      y: 0,
      scale: 1,
      clipPath: 'inset(0% 0% 0% 0%)',
      pointerEvents: this.getPresentationSlidePointerEvents(true),
      zIndex: 2
    });
    gsap.set(nextSlide, {
      autoAlpha: 0,
      y: slideShift * direction,
      scale: 1.035,
      clipPath: direction > 0 ? 'inset(12% 0% 0% 0%)' : 'inset(0% 0% 12% 0%)',
      pointerEvents: this.getPresentationSlidePointerEvents(false),
      zIndex: 3
    });
    this.killPresentationTweens();
    this.animatePresentationExits(currentIndex, direction, outDuration);
    this.presentationTransitionTl?.kill?.();
    this.presentationTransitionTl = gsap.timeline({ defaults: { overwrite: 'auto' } });

    this.presentationTransitionTl.to(currentSlide, {
      autoAlpha: 0,
      y: -slideShift * direction,
      scale: 0.985,
      clipPath: direction > 0 ? 'inset(0% 0% 12% 0%)' : 'inset(12% 0% 0% 0%)',
      duration: outDuration,
      ease: 'power3.inOut',
      onComplete: () =>
        gsap.set(currentSlide, {
          pointerEvents: this.getPresentationSlidePointerEvents(false),
          zIndex: 1
        })
    }, 0);

    this.presentationTransitionTl.fromTo(
      nextSlide,
      {
        autoAlpha: 0,
        y: slideShift * direction,
        scale: 1.035,
        clipPath: direction > 0 ? 'inset(12% 0% 0% 0%)' : 'inset(0% 0% 12% 0%)',
        pointerEvents: this.getPresentationSlidePointerEvents(false)
      },
      {
        autoAlpha: 1,
        y: 0,
        scale: 1,
        clipPath: 'inset(0% 0% 0% 0%)',
        pointerEvents: this.getPresentationSlidePointerEvents(true),
        duration: inDuration,
        ease: 'expo.out',
      },
      overlapStart
    );

    this.activePresentationIndex = index;
    this.animatePresentationChrome(animateTransition);
    this.animatePresentationInternals(index, internalsDelay);
  }

  private killPresentationTweens(): void {
    if (!this.gsapLib || !this.presentationSlideEls.length) return;
    const gsap = this.gsapLib;

    const targets = this.presentationSlideEls.flatMap((slideEl) =>
      Array.from(
        slideEl.querySelectorAll<HTMLElement>(
          '.presentation-eyebrow, .presentation-title-main, .presentation-title-outline, .presentation-description, .presentation-cta, .presentation-visual-orb, .presentation-visual-img, .hero-dashboard-image, .hero-float-card, .presentation-index, .presentation-dual-charts, .presentation-chart-stage, .presentation-chart-panel-title, .presentation-impact-chart-wrap'
        )
      )
    );

    if (targets.length) {
      gsap.killTweensOf(targets);
    }
    gsap.killTweensOf(this.presentationSlideEls);

    this.presentationChromeTl?.kill?.();
    this.presentationChromeTl = null;
  }

  private normalizePresentationSlides(visibleIndex: number, pendingIndex: number | null = null): void {
    if (!this.gsapLib || !this.presentationSlideEls.length) return;
    const gsap = this.gsapLib;

    this.presentationSlideEls.forEach((slideEl, slideIndex) => {
      if (slideIndex === visibleIndex || slideIndex === pendingIndex) return;
      gsap.set(slideEl, {
        autoAlpha: 0,
        y: 0,
        scale: 1,
        clipPath: 'inset(0% 0% 0% 0%)',
        pointerEvents: this.getPresentationSlidePointerEvents(false),
        zIndex: 1
      });
    });
  }

  private animatePresentationChrome(animateTransition: boolean): void {
    if (!this.gsapLib) return;

    const sectionEl = this.chartsPresentation?.nativeElement;
    if (!sectionEl) return;

    if (
      isPlatformBrowser(this.platformId) &&
      typeof window !== 'undefined' &&
      window.matchMedia('(max-width: 767.98px)').matches
    ) {
      this.presentationChromeTl?.kill?.();
      this.presentationChromeTl = null;
      return;
    }

    const gsap = this.gsapLib;
    const bullets = sectionEl.querySelector<HTMLElement>('.presentation-bullets');
    const pagination = sectionEl.querySelector<HTMLElement>('.presentation-index');
    const scrollHint = sectionEl.querySelector<HTMLElement>('.presentation-scroll-hint');
    const scrollText = scrollHint?.querySelector<HTMLElement>('span');
    const scrollLine = sectionEl.querySelector<HTMLElement>('.presentation-scroll-line');
    const social = sectionEl.querySelector<HTMLElement>('.presentation-social');
    const socialItems = social ? Array.from(social.querySelectorAll<HTMLElement>('a')) : [];

    const leftPaginationTargets = [pagination].filter((node): node is HTMLElement => !!node);
    const leftBulletTargets = [bullets].filter((node): node is HTMLElement => !!node);
    const sideUiTargets = [scrollText, scrollLine, ...socialItems].filter((node): node is HTMLElement => !!node);
    const chromeTargets = [...leftPaginationTargets, ...leftBulletTargets, ...sideUiTargets];

    if (!chromeTargets.length) return;

    gsap.killTweensOf(leftPaginationTargets);
    if (!this.hasAnimatedPresentationBullets) {
      gsap.killTweensOf(leftBulletTargets);
    }
    this.presentationChromeTl?.kill?.();
    this.presentationChromeTl = null;

    if (!animateTransition) {
      gsap.set(leftPaginationTargets, { autoAlpha: 1, x: 0 });
      gsap.set(leftBulletTargets, { autoAlpha: 1, x: 0 });
      gsap.set(sideUiTargets, { autoAlpha: 1, x: 0 });
      if (scrollLine) {
        gsap.set(scrollLine, { autoAlpha: 1, scaleY: 1, transformOrigin: 'center center' });
      }
      this.hasAnimatedPresentationBullets = true;
      this.hasAnimatedPresentationSideUi = true;
      return;
    }

    const tl = gsap.timeline({ defaults: { overwrite: 'auto' } });
    this.presentationChromeTl = tl;

    if (!this.hasAnimatedPresentationBullets && leftBulletTargets.length) {
      tl.fromTo(
        leftBulletTargets,
        { autoAlpha: 0, x: -30 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.52,
          ease: 'power2.out'
        },
        0.05
      );
      this.hasAnimatedPresentationBullets = true;
    }

    if (leftPaginationTargets.length) {
      tl.to(
        leftPaginationTargets,
        {
          autoAlpha: 0,
          x: -18,
          duration: 0.22,
          ease: 'power1.in',
          stagger: 0.03
        },
        0
      );
      tl.fromTo(
        leftPaginationTargets,
        { autoAlpha: 0, x: -34 },
        {
          autoAlpha: 1,
          x: 0,
          duration: 0.52,
          ease: 'power2.out',
          stagger: 0.04
        },
        0.22
      );
    }

    if (!this.hasAnimatedPresentationSideUi) {
      if (scrollText) {
        tl.fromTo(
          scrollText,
          { autoAlpha: 0, x: 16 },
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.46,
            ease: 'power2.out'
          },
          0.08
        );
      }

      if (socialItems.length) {
        tl.fromTo(
          socialItems,
          { autoAlpha: 0, x: 18 },
          {
            autoAlpha: 1,
            x: 0,
            duration: 0.48,
            ease: 'power2.out',
            stagger: 0.06
          },
          0.12
        );
      }

      if (scrollLine) {
        tl.fromTo(
          scrollLine,
          { autoAlpha: 0, scaleY: 0, transformOrigin: 'center center' },
          {
            autoAlpha: 1,
            scaleY: 1,
            duration: 0.62,
            ease: 'power3.out',
            transformOrigin: 'center center'
          },
          0.28
        );
      }

      this.hasAnimatedPresentationSideUi = true;
    } else {
      gsap.set(sideUiTargets, { autoAlpha: 1, x: 0 });
      if (scrollLine) {
        gsap.set(scrollLine, { autoAlpha: 1, scaleY: 1, transformOrigin: 'center center' });
      }
    }
  }

  private animatePresentationExits(index: number, direction: number, duration: number): void {
    if (!this.gsapLib || !this.presentationSlideEls[index]) return;

    const gsap = this.gsapLib;
    const slideEl = this.presentationSlideEls[index];
    const caption = slideEl.querySelector<HTMLElement>('.presentation-eyebrow');
    const titleMain = slideEl.querySelector<HTMLElement>('.presentation-title-main');
    const titleHighlight = slideEl.querySelector<HTMLElement>('.presentation-title-outline');
    const subtitle = slideEl.querySelector<HTMLElement>('.presentation-description');
    const cta = slideEl.querySelector<HTMLElement>('.presentation-cta');
    const visual = slideEl.querySelector<HTMLElement>('.presentation-visual-img, .hero-dashboard-image');
    const chartStage = slideEl.querySelector<HTMLElement>('.presentation-chart-stage');
    const chartPanelTitles = slideEl.querySelectorAll<HTMLElement>('.presentation-chart-panel-title');
    const heroCards = slideEl.querySelectorAll<HTMLElement>('.hero-float-card');

    if (caption) {
      gsap.to(caption, {
        autoAlpha: 0,
        y: -30,
        duration: duration * 1.02,
        ease: 'power2.in',
        overwrite: 'auto'
      });
    }

    if (titleMain) {
      if (index === 2 || index === 3) {
        gsap.to(titleMain, {
          autoAlpha: 0,
          y: -26,
          duration: duration * 1.05,
          ease: 'power2.in',
          overwrite: 'auto'
        });
      } else {
        gsap.to(titleMain, {
          autoAlpha: 0,
          x: -68,
          duration: duration * 1.12,
          ease: 'power3.inOut',
          overwrite: 'auto'
        });
      }
    }

    if (titleHighlight) {
      if (index === 2 || index === 3) {
        gsap.to(titleHighlight, {
          autoAlpha: 0,
          y: -18,
          duration: duration * 1.02,
          ease: 'power2.in',
          overwrite: 'auto'
        });
      } else {
        gsap.to(titleHighlight, {
          autoAlpha: 0,
          x: 68,
          duration: duration * 1.12,
          ease: 'power3.inOut',
          overwrite: 'auto'
        });
      }
    }

    if (subtitle) {
      gsap.to(subtitle, {
        autoAlpha: 0,
        y: 42,
        duration: Math.max(0.46, duration * 0.9),
        ease: 'power2.in',
        overwrite: 'auto'
      });
    }

    if (cta) {
      gsap.to(cta, {
        autoAlpha: 0,
        y: 56,
        duration: Math.max(0.66, duration * 1.32),
        ease: 'power3.in',
        overwrite: 'auto'
      });
    }

    if (visual && index === 0) {
      gsap.to(visual, {
        autoAlpha: 0,
        y: -128 * direction,
        scale: 0.92,
        duration: duration * 1.3,
        ease: 'power3.inOut',
        overwrite: 'auto'
      });
    }

    if (visual && index !== 0) {
      gsap.to(visual, {
        autoAlpha: 0,
        y: 72 * direction,
        scale: 0.88,
        rotate: 5 * direction,
        duration: duration,
        ease: 'power3.in',
        overwrite: 'auto'
      });
    }

    if (chartStage) {
      gsap.to(chartStage, {
        autoAlpha: 0,
        x: -52 * direction,
        scale: 0.96,
        duration: duration * 0.95,
        ease: 'power3.in',
        overwrite: 'auto'
      });
    }

    const dualCharts = slideEl.querySelector<HTMLElement>('.presentation-dual-charts');
    if (index === 2 && dualCharts) {
      gsap.to(dualCharts, {
        autoAlpha: 0,
        y: 36 * direction,
        duration: duration * 1.05,
        ease: 'power2.in',
        overwrite: 'auto'
      });
    }

    if (chartPanelTitles.length) {
      gsap.to(chartPanelTitles, {
        autoAlpha: 0,
        y: 18,
        duration: duration * 0.82,
        stagger: 0.04,
        ease: 'power2.in',
        overwrite: 'auto'
      });
    }

    const impactChartWrap = slideEl.querySelector<HTMLElement>('.presentation-impact-chart-wrap');
    if (index === 3 && impactChartWrap) {
      gsap.to(impactChartWrap, {
        autoAlpha: 0,
        y: 36 * direction,
        duration: duration * 1.05,
        ease: 'power2.in',
        overwrite: 'auto'
      });
    }

    if (index === 0 && heroCards.length) {
      const heroExitDistance =
        isPlatformBrowser(this.platformId) && typeof window !== 'undefined'
          ? Math.max(window.innerHeight, 760)
          : 760;

      gsap.to(heroCards, {
        autoAlpha: 0,
        '--hero-card-shift-y': `-${heroExitDistance}px`,
        duration: Math.max(0.9, duration * 1.44),
        stagger: 0.1,
        ease: 'power3.in',
        overwrite: 'auto'
      });
    }
  }

  private animatePresentationInternals(index: number, delay = 0): void {
    if (!this.gsapLib || !this.presentationSlideEls[index]) return;
    const gsap = this.gsapLib;
    const slideEl = this.presentationSlideEls[index];

    if (index === 2) {
      this.prepareBarChartInitialState(this.audienceBarCharts?.age);
      this.prepareBarChartInitialState(this.audienceBarCharts?.gender);
    }

    if (index === 3) {
      this.prepareBarChartInitialState(this.impactBarChart);
    }

    const caption = slideEl.querySelector<HTMLElement>('.presentation-eyebrow');
    const titleMain = slideEl.querySelector<HTMLElement>('.presentation-title-main');
    const titleHighlight = slideEl.querySelector<HTMLElement>('.presentation-title-outline');
    const subtitle = slideEl.querySelector<HTMLElement>('.presentation-description');
    const cta = slideEl.querySelector<HTMLElement>('.presentation-cta');
    const orb = slideEl.querySelector<HTMLElement>('.presentation-visual-orb');
    const visual = slideEl.querySelector<HTMLElement>('.presentation-visual-img, .hero-dashboard-image');
    const chartStage = slideEl.querySelector<HTMLElement>('.presentation-chart-stage');
    const chartCaption = slideEl.querySelector<HTMLElement>('.presentation-chart-caption');
    const chartPanelTitles = slideEl.querySelectorAll<HTMLElement>('.presentation-chart-panel-title');
    const heroCards = slideEl.querySelectorAll<HTMLElement>('.hero-float-card');

    if (caption) {
      gsap.fromTo(
        caption,
        { autoAlpha: 0, y: -42, letterSpacing: '0.56em' },
        {
          autoAlpha: 1,
          y: 0,
          letterSpacing: index === 3 ? '0.38em' : '0.42em',
          duration: 0.9,
          ease: 'power3.out',
          delay,
          overwrite: 'auto'
        }
      );
    }

    if (titleMain) {
      if (index === 2 || index === 3) {
        gsap.fromTo(
          titleMain,
          { autoAlpha: 0, y: 54, scale: 0.94 },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 1.08,
            ease: 'expo.out',
            delay: delay + 0.14,
            overwrite: 'auto'
          }
        );
      } else {
        gsap.fromTo(
          titleMain,
          { autoAlpha: 0, x: -112, scale: 0.96 },
          {
            autoAlpha: 1,
            x: 0,
            scale: 1,
            duration: 1.12,
            ease: 'expo.out',
            delay: delay + 0.14,
            overwrite: 'auto'
          }
        );
      }
    }

    if (titleHighlight) {
      if (index === 2 || index === 3) {
        gsap.fromTo(
          titleHighlight,
          { autoAlpha: 0, y: 46, scale: 0.96 },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            duration: 1.08,
            ease: 'expo.out',
            delay: delay + 0.2,
            overwrite: 'auto'
          }
        );
      } else {
        gsap.fromTo(
          titleHighlight,
          { autoAlpha: 0, x: 112, scale: 0.96 },
          {
            autoAlpha: 1,
            x: 0,
            scale: 1,
            duration: 1.12,
            ease: 'expo.out',
            delay: delay + 0.2,
            overwrite: 'auto'
          }
        );
      }
    }

    if (subtitle) {
      gsap.fromTo(
        subtitle,
        { autoAlpha: 0, y: 64 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.82,
          ease: 'power3.out',
          delay: delay + 0.36,
          overwrite: 'auto'
        }
      );
    }

    if (cta) {
      gsap.fromTo(
        cta,
        { autoAlpha: 0, y: 74, scale: 0.92 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          duration: 1.16,
          ease: 'expo.out',
          delay: delay + 0.5,
          overwrite: 'auto'
        }
      );
    }

    if (orb) {
      gsap.fromTo(
        orb,
        { autoAlpha: 0.08, scale: 0.62, rotate: -10 },
        {
          autoAlpha: 1,
          scale: 1,
          rotate: 0,
          duration: 1.22,
          ease: 'expo.out',
          delay: delay + 0.28
        }
      );
    }

    if (visual) {
      const heroEntryDistance =
        isPlatformBrowser(this.platformId) && typeof window !== 'undefined'
          ? Math.max(window.innerHeight, 760)
          : 760;

      gsap.fromTo(
        visual,
        index === 0
          ? { autoAlpha: 0, y: -heroEntryDistance, scale: 0.86, rotate: -3 }
          : { autoAlpha: 0, y: 80, scale: 0.84, rotate: -7 },
        {
          autoAlpha: 1,
          y: 0,
          scale: 1,
          rotate: 0,
          duration: index === 0 ? 1.62 : 1.18,
          ease: 'expo.out',
          delay: delay + 0.38,
          overwrite: 'auto'
        }
      );
    }

    if (chartStage) {
      gsap.fromTo(
        chartStage,
        { autoAlpha: 0, x: -86, scale: 0.96, clipPath: 'inset(0% 100% 0% 0%)' },
        {
          autoAlpha: 1,
          x: 0,
          scale: 1,
          clipPath: 'inset(0% 0% 0% 0%)',
          duration: 1.08,
          ease: 'expo.out',
          delay: delay + 0.2,
          overwrite: 'auto'
        }
      );
    }

    if (chartCaption) {
      gsap.fromTo(
        chartCaption,
        { autoAlpha: 0, y: 12 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          delay: delay + 0.92,
          overwrite: 'auto'
        }
      );
    }

    if (index === 0 && heroCards.length) {
      gsap.fromTo(
        heroCards,
        { autoAlpha: 0, '--hero-card-shift-y': '-76px', scale: 0.86 },
        {
          autoAlpha: 1,
          '--hero-card-shift-y': '0px',
          scale: 1,
          duration: 1.18,
          ease: 'expo.out',
          stagger: 0.12,
          delay: delay + 0.64,
          overwrite: 'auto'
        }
      );
    }

    if (index === 0 && !this.hasDispatchedHeroIntroReady) {
      const heroCardsEnd = heroCards.length
        ? delay + 0.64 + 1.18 + Math.max(0, heroCards.length - 1) * 0.12
        : 0;
      const visualEnd = visual ? delay + 0.38 + 1.62 : 0;
      const ctaEnd = cta ? delay + 0.5 + 1.16 : 0;
      const titleEnd = delay + 0.2 + 1.12;
      const introEndMs = Math.round((Math.max(heroCardsEnd, visualEnd, ctaEnd, titleEnd) + 0.45) * 1000);
      this.scheduleHeroIntroReady(introEndMs);
    }

    if (index === 2) {
      const dualCharts = slideEl.querySelector<HTMLElement>('.presentation-dual-charts');
      if (dualCharts) {
        gsap.fromTo(
          dualCharts,
          { autoAlpha: 0, y: 0, scale: 1, clipPath: 'inset(0% 0% 0% 0%)' },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 0.36,
            ease: 'power1.out',
            delay: delay + 0.18,
            overwrite: 'auto'
          }
        );
      }
      if (chartPanelTitles.length) {
        gsap.fromTo(
          chartPanelTitles,
          { autoAlpha: 0, y: 18 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.6,
            ease: 'power2.out',
            stagger: 0.08,
            delay: delay + 0.82,
            overwrite: 'auto'
          }
        );
      }
      window.setTimeout(() => this.replayAudienceChartsIntro(), Math.round((delay + 0.32) * 1000));
    }

    if (index === 3) {
      const impactChartWrap = slideEl.querySelector<HTMLElement>('.presentation-impact-chart-wrap');
      if (impactChartWrap) {
        gsap.fromTo(
          impactChartWrap,
          { autoAlpha: 0, y: 26, scale: 0.97, filter: 'blur(6px)', clipPath: 'inset(0% 0% 0% 0%)' },
          {
            autoAlpha: 1,
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
            clipPath: 'inset(0% 0% 0% 0%)',
            duration: 1.05,
            ease: 'expo.out',
            delay: delay + 0.32,
            overwrite: 'auto'
          }
        );
      }
      window.setTimeout(() => this.replayImpactBarChartIntro(), Math.round((delay + 0.68) * 1000));
    }

    if (index === 1) {
      this.replayMarketChartIntro();
    }
  }

  onHeroTrackerMove(event: MouseEvent): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (window.matchMedia('(hover: none)').matches) return;

    const surface = event.currentTarget as HTMLElement | null;
    if (!surface) return;

    const bounds = surface.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;

    const pointerX = (event.clientX - bounds.left) / bounds.width;
    const pointerY = (event.clientY - bounds.top) / bounds.height;
    const x = Math.min(1, Math.max(0, pointerX));
    const y = Math.min(1, Math.max(0, pointerY));

    const centerX = (x - 0.5) * 2;
    const centerY = (y - 0.5) * 2;
    const maxRotate = 5.5;
    const maxShift = 9;

    this.heroTrackerRotateY = `${(centerX * maxRotate).toFixed(2)}deg`;
    this.heroTrackerRotateX = `${(-centerY * maxRotate * 0.75).toFixed(2)}deg`;
    this.heroTrackerOffsetX = `${(centerX * maxShift).toFixed(2)}px`;
    this.heroTrackerOffsetY = `${(centerY * maxShift * 0.65).toFixed(2)}px`;
    this.heroTrackerGlowX = `${(x * 100).toFixed(1)}%`;
    this.heroTrackerGlowY = `${(y * 100).toFixed(1)}%`;
  }

  resetHeroTracker(): void {
    this.heroTrackerRotateX = '0deg';
    this.heroTrackerRotateY = '0deg';
    this.heroTrackerOffsetX = '0px';
    this.heroTrackerOffsetY = '0px';
    this.heroTrackerGlowX = '50%';
    this.heroTrackerGlowY = '50%';
  }

  private setupLogoSwiper(): void {
    const swiperEl = this.logoSwiper?.nativeElement as any;
    const nextEl = this.nextBtn?.nativeElement;
    const prevEl = this.prevBtn?.nativeElement;
    if (!swiperEl || !nextEl || !prevEl) return;

    Object.assign(swiperEl, {
      loop: true,
      speed: 700,
      grabCursor: true,
      spaceBetween: 0,
      slidesPerView: 1,
      observer: true,
      observeParents: true,
      watchOverflow: true,
      autoplay: {
        delay: 2200,
        disableOnInteraction: false,
        pauseOnMouseEnter: true,
      },
      navigation: {
        nextEl,
        prevEl,
      },
      breakpoints: {
        480: { slidesPerView: 2 },
        768: { slidesPerView: 3 },
        992: { slidesPerView: 4 },
        1200: { slidesPerView: 5 },
      },
    });

    swiperEl.initialize();
  }

  private async setupApproachTimelineAnimation(): Promise<void> {
    const timelineEl = this.approachTimeline?.nativeElement;
    if (!timelineEl) return;

    const [{ gsap }, { ScrollTrigger }] = await Promise.all([
      import('gsap'),
      import('gsap/ScrollTrigger')
    ]);

    gsap.registerPlugin(ScrollTrigger);

    const items = Array.from(timelineEl.querySelectorAll<HTMLElement>('.approach-timeline-item'));
    const cards = items
      .map((item) => item.querySelector<HTMLElement>('.approach-timeline-card'))
      .filter((value): value is HTMLElement => !!value);
    const dots = items
      .map((item) => item.querySelector<HTMLElement>('.approach-timeline-dot'))
      .filter((value): value is HTMLElement => !!value);
    const icons = items
      .map((item) => item.querySelector<HTMLElement>('.approach-timeline-icon'))
      .filter((value): value is HTMLElement => !!value);
    const progress = timelineEl.querySelector<HTMLElement>('.approach-timeline-progress');
    const stepNumbers = Array.from(timelineEl.querySelectorAll<HTMLElement>('.approach-step-number'));

    if (!items.length || !cards.length || !dots.length) return;

    let mediaMatcher: any = null;

    const setActiveStep = (activeIndex: number): void => {
      items.forEach((item, index) => {
        item.classList.toggle('is-active', index === activeIndex);
        item.classList.toggle('is-past', index < activeIndex);
      });
    };

    const ctx = gsap.context(() => {
      const reduceMotion =
        isPlatformBrowser(this.platformId) &&
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      setActiveStep(0);

      if (reduceMotion) {
        gsap.set(cards, { clearProps: 'all', opacity: 1, y: 0, x: 0, scale: 1 });
        gsap.set(dots, { clearProps: 'all', opacity: 1, scale: 1 });
        gsap.set(icons, { clearProps: 'all', rotate: 0, scale: 1 });
        gsap.set(stepNumbers, { clearProps: 'all', opacity: 1, y: 0 });
        if (progress) {
          gsap.set(progress, { clearProps: 'all', scaleY: 1 });
        }
        return;
      }

      mediaMatcher = gsap.matchMedia();

      if (progress) {
        gsap.fromTo(
          progress,
          { scaleY: 0, transformOrigin: 'top center' },
          {
            scaleY: 1,
            ease: 'none',
            scrollTrigger: {
              trigger: timelineEl,
              start: 'top 76%',
              end: 'bottom 26%',
              scrub: 0.75
            }
          }
        );

        gsap.to(progress, {
          backgroundPosition: '0% 100%',
          ease: 'none',
          scrollTrigger: {
            trigger: timelineEl,
            start: 'top bottom',
            end: 'bottom top',
            scrub: true
          }
        });
      }

      mediaMatcher.add('(min-width: 992px)', () => {
        items.forEach((item, index) => {
          const card = cards[index];
          const dot = dots[index];
          const icon = icons[index];
          const stepNumber = stepNumbers[index];

          gsap.set(card, { autoAlpha: 0.34, y: 82, x: 54, scale: 0.96 });
          gsap.set(dot, { autoAlpha: 0.46, scale: 0.72 });
          if (stepNumber) {
            gsap.set(stepNumber, { autoAlpha: 0.18, y: 18 });
          }
          if (icon) {
            gsap.set(icon, { rotate: index % 2 === 0 ? -18 : 18, scale: 0.72, transformOrigin: '50% 50%' });
          }

          gsap.to(card, {
            autoAlpha: 1,
            y: 0,
            x: 0,
            scale: 1,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: item,
              start: 'top 84%',
              end: 'top 48%',
              scrub: 0.5
            }
          });

          if (stepNumber) {
            gsap.to(stepNumber, {
              autoAlpha: 1,
              y: 0,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: item,
                start: 'top 84%',
                end: 'top 50%',
                scrub: 0.4
              }
            });
          }

          gsap.to(dot, {
            autoAlpha: 1,
            scale: 1,
            ease: 'back.out(1.65)',
            scrollTrigger: {
              trigger: item,
              start: 'top 82%',
              end: 'top 54%',
              scrub: 0.35
            }
          });

          if (icon) {
            gsap.to(icon, {
              rotate: 0,
              scale: 1,
              ease: 'power2.out',
              scrollTrigger: {
                trigger: item,
                start: 'top 82%',
                end: 'top 54%',
                scrub: 0.3
              }
            });
          }

          gsap.to(card, {
            yPercent: index % 2 === 0 ? -4 : 4,
            ease: 'none',
            scrollTrigger: {
              trigger: timelineEl,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true
            }
          });

          ScrollTrigger.create({
            trigger: item,
            start: 'top 58%',
            end: 'bottom 42%',
            onEnter: () => setActiveStep(index),
            onEnterBack: () => setActiveStep(index),
          });
        });

        ScrollTrigger.create({
          trigger: timelineEl,
          start: 'top 58%',
          end: 'bottom 42%',
          onLeave: () => setActiveStep(items.length - 1),
          onLeaveBack: () => setActiveStep(0),
        });
      });

      mediaMatcher.add('(max-width: 991.98px)', () => {
        items.forEach((item, index) => {
          const card = cards[index];
          const dot = dots[index];
          const icon = icons[index];
          const stepNumber = stepNumbers[index];
          const alreadyVisible = this.isApproachItemInView(item, 0.96);

          if (alreadyVisible) {
            gsap.set(card, { autoAlpha: 1, y: 0, scale: 1 });
            if (stepNumber) {
              gsap.set(stepNumber, { autoAlpha: 1, y: 0 });
            }
            gsap.set(dot, { autoAlpha: 1, scale: 1 });
            if (icon) {
              gsap.set(icon, { rotate: 0, scale: 1 });
            }
          } else {
            gsap.fromTo(
              card,
              { autoAlpha: 0, y: 32, scale: 0.98 },
              {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 0.52,
                ease: 'power3.out',
                scrollTrigger: {
                  trigger: item,
                  start: 'top 94%',
                  toggleActions: 'play none none none',
                  once: true,
                  invalidateOnRefresh: true
                }
              }
            );

            if (stepNumber) {
              gsap.fromTo(
                stepNumber,
                { autoAlpha: 0, y: 12 },
                {
                  autoAlpha: 1,
                  y: 0,
                  duration: 0.48,
                  ease: 'power3.out',
                  scrollTrigger: {
                    trigger: item,
                    start: 'top 94%',
                    toggleActions: 'play none none none',
                    once: true,
                    invalidateOnRefresh: true
                  }
                }
              );
            }

            gsap.fromTo(
              dot,
              { autoAlpha: 0.45, scale: 0.7 },
              {
                autoAlpha: 1,
                scale: 1,
                duration: 0.42,
                ease: 'back.out(1.6)',
                scrollTrigger: {
                  trigger: item,
                  start: 'top 93%',
                  toggleActions: 'play none none none',
                  once: true,
                  invalidateOnRefresh: true
                }
              }
            );

            if (icon) {
              gsap.fromTo(
                icon,
                { rotate: -14, scale: 0.78 },
                {
                  rotate: 0,
                  scale: 1,
                  duration: 0.42,
                  ease: 'power2.out',
                  scrollTrigger: {
                    trigger: item,
                    start: 'top 93%',
                    toggleActions: 'play none none none',
                    once: true,
                    invalidateOnRefresh: true
                  }
                }
              );
            }
          }

          ScrollTrigger.create({
            trigger: item,
            start: 'top 78%',
            end: 'bottom 35%',
            invalidateOnRefresh: true,
            onEnter: () => setActiveStep(index),
            onEnterBack: () => setActiveStep(index)
          });
        });

        ScrollTrigger.create({
          trigger: timelineEl,
          start: 'top 80%',
          end: 'bottom 35%',
          invalidateOnRefresh: true,
          onLeave: () => setActiveStep(items.length - 1),
          onLeaveBack: () => setActiveStep(0)
        });
      });
    }, timelineEl);

    this.gsapCleanups.push(() => {
      mediaMatcher?.revert?.();
      ctx.revert();
    });

    const syncVisible = () =>
      this.syncApproachTimelineVisibleState(gsap, items, cards, dots, icons, stepNumbers, setActiveStep);

    requestAnimationFrame(() => {
      void this.refreshAllScrollTriggers(ScrollTrigger);
      syncVisible();
    });
    window.setTimeout(() => {
      void this.refreshAllScrollTriggers(ScrollTrigger);
      syncVisible();
    }, 120);
    window.setTimeout(() => {
      void this.refreshAllScrollTriggers(ScrollTrigger);
      syncVisible();
    }, 450);
  }

  private safeRefreshScrollTrigger(ScrollTrigger: { refresh: (safe?: boolean) => void }): void {
    void this.refreshAllScrollTriggers(ScrollTrigger);
  }

  private easeOutCubic(t: number): number {
    return 1 - Math.pow(1 - t, 3);
  }

  private animateCounters(): void {
    if (this.counterAnimationFrame !== null) {
      cancelAnimationFrame(this.counterAnimationFrame);
      this.counterAnimationFrame = null;
    }

    this.resetCounters(false);
    const duration = 1800;
    const startTime = performance.now();

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = this.easeOutCubic(progress);

      this.stats.forEach((stat, i) => {
        this.currentValues[i] = Math.round(stat.target * eased);
      });

      if (progress < 1) {
        this.counterAnimationFrame = requestAnimationFrame(tick);
      } else {
        this.stats.forEach((stat, i) => {
          this.currentValues[i] = stat.target;
        });
        this.counterAnimationFrame = null;
      }
    };

    this.counterAnimationFrame = requestAnimationFrame(tick);
  }

  private resetCounters(cancelFrame = true): void {
    if (cancelFrame && this.counterAnimationFrame !== null) {
      cancelAnimationFrame(this.counterAnimationFrame);
      this.counterAnimationFrame = null;
    }
    this.currentValues = this.stats.map(() => 0);
  }
}
